import type { BuildChange } from '../games/build-timeline.js';

// A feed row after build dividers have been merged in: either an original
// item or a "build changed" divider.
export type InterleavedRow<T> =
  | { kind: 'item'; item: T }
  | { kind: 'build'; change: BuildChange };

function ms(ts: string): number {
  const t = Date.parse(ts);
  return Number.isNaN(t) ? 0 : t;
}

// Merge build-change divider rows into a time-DESCENDING list of feed
// items, each divider landing at its chronological slot.
//
// - A change newer than the newest item surfaces at the top.
// - A change between two items appears between them.
// - A change at or older than the oldest loaded item is omitted: it
//   belongs to a page the feed hasn't loaded yet, and dumping every
//   historical build at the bottom of a partial feed would be noise. It
//   reappears once older items page in.
//
// `getTime` returns the item's chronological timestamp (e.g. a group's
// chronologicalAt or a submission's submittedAt). Comparisons go through
// Date.parse so mixed UTC offsets compare by instant, not by string.
export function interleaveBuildMarkers<T>(
  items: T[],
  changes: BuildChange[],
  getTime: (item: T) => string,
): InterleavedRow<T>[] {
  const sorted = [...changes].sort((a, b) => ms(b.changedAt) - ms(a.changedAt));
  const out: InterleavedRow<T>[] = [];
  let ci = 0;
  for (const item of items) {
    const itemTime = ms(getTime(item));
    while (ci < sorted.length && ms(sorted[ci]!.changedAt) > itemTime) {
      out.push({ kind: 'build', change: sorted[ci]! });
      ci += 1;
    }
    out.push({ kind: 'item', item });
  }
  // Any changes left are at or older than the oldest item - omitted.
  return out;
}
